// Orders.js - Filter Bar + row checkboxes + bulk Delete
// + bulk "Send Invoice": modal, customer-wise grouping, ONE email per customer (all PDFs attached)

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaSearch, FaEdit, FaBuilding, FaMapMarkerAlt, FaFilter, FaUndo, FaEnvelope, FaTrash,
  FaTimes, FaUser, FaFileInvoice, FaInfoCircle, FaExclamationTriangle
} from "react-icons/fa";
import { RiDeleteBin5Fill } from 'react-icons/ri';
import { FaBoxOpen } from 'react-icons/fa';
import { IoMdArrowDropdown, IoMdSend } from "react-icons/io";
import axios from 'axios';
import Swal from 'sweetalert2';
import { useCurrency } from '../../context/CurrencyContext';
import { buildInvoicePdfBase64, getLogoBase64, getInvoiceNumber, transformOrderedProducts } from './invoicePdf';
import './Orders.css';

const API_BASE_URL = 'https://super-backend.hira-tech.co.uk';

const api = axios.create({ baseURL: API_BASE_URL });

const getAuthToken = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token') || localStorage.getItem('accessToken');
  if (token) return token;

  const possibleKeys = ['token', 'authToken', 'access_token', 'accessToken', 'jwt'];
  for (let key of possibleKeys) {
    const storedData = localStorage.getItem(key);
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (parsed.access || parsed.token || parsed.access_token) {
          return parsed.access || parsed.token || parsed.access_token;
        }
      } catch (e) { return storedData; }
    }
  }
  return null;
};

api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Readable error message from backend (HTML error pages are ignored)
const extractServerErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (!data) return error?.message || fallback;
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (trimmed.startsWith('<')) return fallback;
    return trimmed || fallback;
  }
  if (data.error) return data.error;
  if (data.message) return data.message;
  if (data.detail) return data.detail;
  return fallback;
};

const escapeHtml = (v) =>
  String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// Same customer email -> same key, even with different casing / stray spaces
const normalizeEmail = (email) => (typeof email === 'string' ? email.trim().toLowerCase() : '');
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const hasBankInfo = (d) => !!(d && (d.bank_name || d.account_number || d.iban || d.account_title));

// Short list for popups: "HT1, HT2, HT3 +4 more"
const previewList = (orders, max = 6) => {
  const ids = orders.map(o => escapeHtml(o.order_Tracking_Id || o.order_Id));
  return ids.length > max ? `${ids.slice(0, max).join(', ')} +${ids.length - max} more` : ids.join(', ');
};

// Last loaded orders of each shop are kept, so the list shows instantly
// on the next visit while fresh data loads in the background.
const ORDERS_CACHE_PREFIX = 'orders_cache_';

const readOrdersCache = (id) => {
  if (!id) return null;
  try {
    const raw = localStorage.getItem(`${ORDERS_CACHE_PREFIX}${id}`);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && Array.isArray(parsed.orders) ? parsed : null;
  } catch (e) {
    return null;
  }
};

const writeOrdersCache = (id, orders, cityCounts) => {
  if (!id) return;
  try {
    localStorage.setItem(`${ORDERS_CACHE_PREFIX}${id}`, JSON.stringify({ orders, cityCounts }));
  } catch (e) {}
};

const getUrlShopId = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const queryShopId = urlParams.get('shop_id');
  if (queryShopId) return queryShopId;

  const pathMatch = window.location.pathname.match(/\/shop\/(\d+)/);
  if (pathMatch) return pathMatch[1];

  return null;
};

const Orders = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { formatPrice, getCurrencySymbol, getCurrentCurrency } = useCurrency();

  // The shop is known from the very first render (from the URL), and the saved
  // list of that shop is shown instantly while fresh data loads in the background.
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const startShopId = getUrlShopId();
    initialRef.current = { shopId: startShopId, cache: readOrdersCache(startShopId) };
  }

  const [currentPage, setCurrentPage] = useState(1);
  const [orders, setOrders] = useState(initialRef.current.cache ? initialRef.current.cache.orders : []);
  // True only while there is nothing to show yet (so "No orders found" never flashes)
  const [isLoading, setIsLoading] = useState(!initialRef.current.cache);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [shops, setShops] = useState([]);
  const [selectedShopId, setSelectedShopId] = useState(
    initialRef.current.shopId ? String(initialRef.current.shopId) : ''
  );
  const [shopsLoaded, setShopsLoaded] = useState(!!initialRef.current.shopId);
  const [cityFilter, setCityFilter] = useState('');
  const [cityCounts, setCityCounts] = useState(
    initialRef.current.cache ? (initialRef.current.cache.cityCounts || {}) : {}
  );

  const [customerFilter, setCustomerFilter] = useState('');

  // Row selection (bulk Send Invoice / Delete)
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Send Invoice modal
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendNote, setSendNote] = useState('');

  const ordersRequestRef = useRef(0);
  const prevShopRef = useRef(initialRef.current.shopId ? String(initialRef.current.shopId) : undefined);

  const itemsPerPage = 8;

  const getShopIdFromUrl = useCallback(() => getUrlShopId(), []);

  const fetchShops = async () => {
    try {
      const response = await api.get('/user/shops/');
      let shopsData = [];
      if (response.data.results) shopsData = response.data.results;
      else if (Array.isArray(response.data)) shopsData = response.data;
      else if (response.data.shops) shopsData = response.data.shops;
      setShops(shopsData);
      return shopsData;
    } catch (error) {
      return [];
    }
  };

  const fetchOrders = useCallback(async () => {
    // Only the latest request is allowed to update the screen
    const requestId = ++ordersRequestRef.current;

    try {
      const currentShopId = getShopIdFromUrl() || selectedShopId;

      const url = currentShopId
        ? `/order/getAllorder/?shop_id=${currentShopId}`
        : '/order/getAllorder/';

      const response = await api.get(url);
      if (requestId !== ordersRequestRef.current) return;

      let ordersData = [];
      let cityCountsData = {};

      if (response.data && response.data.orders) {
        ordersData = response.data.orders;
        cityCountsData = response.data.city_counts || {};
      } else if (Array.isArray(response.data)) {
        ordersData = response.data;
      } else if (response.data && response.data.results) {
        ordersData = response.data.results;
      }

      // Newest order first
      ordersData.sort((a, b) => {
        const idA = parseInt(a.order_Id) || 0;
        const idB = parseInt(b.order_Id) || 0;
        return idB - idA;
      });

      setOrders(ordersData);
      setCityCounts(cityCountsData);

      if (currentShopId) writeOrdersCache(currentShopId, ordersData, cityCountsData);

    } catch (error) {
      if (requestId !== ordersRequestRef.current) return;
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch orders. Please try again.',
        confirmButtonColor: '#004aad'
      });
    } finally {
      if (requestId === ordersRequestRef.current) setIsLoading(false);
    }
  }, [selectedShopId, getShopIdFromUrl]);

  // Cities and customers of the loaded orders (used by the dropdowns)
  const availableCities = useMemo(() => {
    return orders
      .map(order => order.city)
      .filter(city => city && city !== '' && city !== 'All Cities')
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
  }, [orders]);

  const availableCustomers = useMemo(() => {
    return orders
      .map(order => {
        if (!order.customer) return null;
        const fName = order.customer.first_Name || order.customer.firstName || '';
        const lName = order.customer.last_Name || order.customer.lastName || '';
        const name = `${fName} ${lName}`.trim();
        const email = order.customer.email || '';

        if (!name && !email) return null;
        if (name && email) return `${name} (${email})`;
        return name || email;
      })
      .filter(val => val && val !== '')
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
  }, [orders]);

  useEffect(() => {
    if (shops.length > 0 && !selectedShopId && !shopsLoaded) {
      const firstShop = shops[0];
      const shopId = firstShop.shop_id || firstShop.id || firstShop._id;
      if (shopId) {
        setSelectedShopId(String(shopId));
        setShopsLoaded(true);
      }
    }
  }, [shops, selectedShopId, shopsLoaded]);

  useEffect(() => {
    const shopIdFromUrl = getShopIdFromUrl();
    if (shopIdFromUrl) {
      setSelectedShopId(shopIdFromUrl);
      setShopsLoaded(true);
    }

    setSearchTerm('');
    setStatusFilter('');
    setCityFilter('');
    setCustomerFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    setSelectedIds(new Set());

  }, [location.pathname, location.search, getShopIdFromUrl]);

  // Shops list is loaded once (it does not block the orders any more)
  useEffect(() => {
    const loadShops = async () => {
      const shopsData = await fetchShops();

      // No shop in the URL and no shops at all: load the orders without a shop
      if (!getShopIdFromUrl() && (!shopsData || shopsData.length === 0)) {
        await fetchOrders();
      }
    };
    loadShops();
  }, []);

  // Orders: show the saved list of the shop immediately, then refresh it
  useEffect(() => {
    const currentShopId = getShopIdFromUrl() || selectedShopId;
    if (!currentShopId) return; // wait until the shop is known

    if (prevShopRef.current !== String(currentShopId)) {
      prevShopRef.current = String(currentShopId);
      const cached = readOrdersCache(currentShopId);
      if (cached) {
        setOrders(cached.orders);
        setCityCounts(cached.cityCounts || {});
        setIsLoading(false);
      } else {
        setOrders([]);
        setCityCounts({});
        setIsLoading(true);
      }
    }

    fetchOrders();
  }, [fetchOrders]);

  // Listen to custom event to re-fetch fresh data
  useEffect(() => {
    const handleOrderUpdate = () => {
      fetchOrders();
    };

    window.addEventListener('orderUpdated', handleOrderUpdate);
    window.addEventListener('focus', handleOrderUpdate);

    return () => {
      window.removeEventListener('orderUpdated', handleOrderUpdate);
      window.removeEventListener('focus', handleOrderUpdate);
    };
  }, [fetchOrders]);

  const filteredOrders = useMemo(() => {
    let filtered = [...orders];

    const currentShopId = getShopIdFromUrl() || selectedShopId;

    if (currentShopId) {
      filtered = filtered.filter(order => {
        const shopId = order.shop_id ||
                      (order.shop_details && order.shop_details.shop_id) ||
                      (order.shop && order.shop.shop_id) ||
                      (order.shop && order.shop.id);
        return String(shopId) === String(currentShopId);
      });
    }

    if (cityFilter) {
      filtered = filtered.filter(order => {
        const orderCity = order.city || 'All Cities';
        return orderCity === cityFilter;
      });
    }

    if (customerFilter) {
      filtered = filtered.filter(order => {
        if (!order.customer) return false;
        const fName = order.customer.first_Name || order.customer.firstName || '';
        const lName = order.customer.last_Name || order.customer.lastName || '';
        const name = `${fName} ${lName}`.trim();
        const email = order.customer.email || '';

        let customerString = '';
        if (name && email) customerString = `${name} (${email})`;
        else customerString = name || email;

        return customerString === customerFilter;
      });
    }

    if (searchTerm) {
      filtered = filtered.filter(order =>
        (order.order_Tracking_Id && order.order_Tracking_Id.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (order.customer_Reference && order.customer_Reference.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (order.customer && order.customer.first_Name && order.customer.first_Name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (order.customer && order.customer.last_Name && order.customer.last_Name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(order => order.order_Status === statusFilter);
    }

    if (startDate) filtered = filtered.filter(order => order.order_Placment_Date >= startDate);
    if (endDate) filtered = filtered.filter(order => order.order_Placment_Date <= endDate);

    return filtered;
  }, [orders, searchTerm, statusFilter, cityFilter, customerFilter, startDate, endDate, location.pathname, location.search, getShopIdFromUrl, selectedShopId]);

  // Go back to page 1 whenever the list or a filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [orders, searchTerm, statusFilter, cityFilter, customerFilter, startDate, endDate, location.pathname, location.search, selectedShopId]);

  // Selection is cleared when a filter changes (not when the list refreshes in the background)
  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchTerm, statusFilter, cityFilter, customerFilter, startDate, endDate]);

  const formatOrderTotal = (totalAmount) => {
    if (totalAmount === undefined || totalAmount === null) {
      return `${getCurrencySymbol()}0.00`;
    }
    return formatPrice(totalAmount);
  };

  const calculateOrderTotal = (order) => {
    return parseFloat(order.total_Amount) || 0;
  };

  // Only show the main order note (from Place Order form).
  const getOrderNoteDisplay = (order) => {
    if (order.note && order.note.trim()) {
      return order.note.trim();
    }
    return '-';
  };

  const handleEditClick = (order) => {
    navigate(`/order-detail/${order.order_Tracking_Id}`, { state: order });
  };

  const handleDeleteClick = async (order) => {
    const result = await Swal.fire({
      title: 'Delete Order',
      text: `Are you sure you want to delete order ${order.order_Tracking_Id || ''}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#004aad',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/order/delete/${order.order_Id}/`);
        setSelectedIds(prev => {
          const next = new Set(prev);
          next.delete(order.order_Id);
          return next;
        });
        await fetchOrders();
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: `Order ${order.order_Tracking_Id} has been deleted.`,
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete order. Please try again.',
          confirmButtonColor: '#004aad'
        });
      }
    }
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setStartDate('');
    setEndDate('');
    setCityFilter('');
    setCustomerFilter('');
    setSearchTerm('');
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  // Filters already apply live; this button selects every order that matches them
  const handleApplyFilters = () => {
    setSelectedIds(new Set(filteredOrders.map(o => o.order_Id)));
  };

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentOrders = filteredOrders.slice(startIndex, endIndex);

  const goToPreviousPage = () => { if (currentPage > 1) setCurrentPage(currentPage - 1); };
  const goToNextPage = () => { if (currentPage < totalPages) setCurrentPage(currentPage + 1); };

  // ============================================================
  // SELECTION
  // ============================================================
  const selectedOrders = useMemo(
    () => orders.filter(o => selectedIds.has(o.order_Id)),
    [orders, selectedIds]
  );
  const selectedCount = selectedOrders.length;

  const pageSelectedCount = currentOrders.filter(o => selectedIds.has(o.order_Id)).length;
  const allPageSelected = currentOrders.length > 0 && pageSelectedCount === currentOrders.length;
  const somePageSelected = pageSelectedCount > 0 && !allPageSelected;

  const toggleOrder = (orderId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const togglePage = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allPageSelected) currentOrders.forEach(o => next.delete(o.order_Id));
      else currentOrders.forEach(o => next.add(o.order_Id));
      return next;
    });
  };

  const removeFromSelection = (list) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      list.forEach(o => next.delete(o.order_Id));
      return next;
    });
  };

  const updateProgress = (done) => {
    const el = Swal.getHtmlContainer()?.querySelector('#bulk-progress');
    if (el) el.textContent = String(done);
  };

  // ============================================================
  // SEND INVOICES: group the selected orders by customer email
  // ============================================================
  const groupedOrders = useMemo(() => {
    const groups = {};
    selectedOrders.forEach((order) => {
      const rawEmail = order.customer?.email || '';
      const key = normalizeEmail(rawEmail) || 'no-email';
      if (!groups[key]) {
        const name = order.customer
          ? `${order.customer.first_Name || ''} ${order.customer.last_Name || ''}`.trim()
          : '';
        groups[key] = {
          key,
          email: rawEmail.trim(),
          customerName: name || 'Unknown',
          valid: key !== 'no-email' && isValidEmail(key),
          orders: [],
        };
      }
      groups[key].orders.push(order);
    });
    return Object.values(groups);
  }, [selectedOrders]);

  const sendableGroups = groupedOrders.filter(g => g.valid);
  const skippedGroups = groupedOrders.filter(g => !g.valid);
  const sendableOrdersCount = sendableGroups.reduce((n, g) => n + g.orders.length, 0);
  const sendableTotal = sendableGroups.reduce(
    (sum, g) => sum + g.orders.reduce((s, o) => s + (parseFloat(o.total_Amount) || 0), 0), 0
  );

  const openSendModal = () => {
    if (bulkBusy || selectedCount === 0) return;
    setShowSendModal(true);
  };

  const closeSendModal = () => {
    if (!bulkBusy) setShowSendModal(false);
  };

  const handleConfirmSend = async () => {
    if (bulkBusy) return;

    const groups = sendableGroups;
    const skipped = skippedGroups.flatMap(g => g.orders);
    const note = sendNote.trim();

    if (groups.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Valid Email Address',
        text: 'None of the selected orders has a valid customer email address.',
        confirmButtonColor: '#004aad'
      });
      return;
    }

    const totalInvoices = groups.reduce((n, g) => n + g.orders.length, 0);

    setShowSendModal(false);
    setBulkBusy(true);
    Swal.fire({
      title: 'Sending Invoices...',
      html: `<div style="font-size:15px"><b id="bulk-progress">0</b> / ${totalInvoices} invoice(s) prepared</div>
             <p style="font-size:13px;color:#64748b;margin-top:8px">Please do not close this page.</p>`,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => { Swal.showLoading(); }
    });

    const logo = await getLogoBase64();

    // Company details are loaded once per (shop + city)
    const companyCache = new Map();
    const getCompany = async (order) => {
      const shopId = order.shop_id || order.shop_details?.shop_id || getUrlShopId() || selectedShopId;
      const city = order.city || order.address?.city || '';
      const cacheKey = `${shopId}|${city}`;
      if (companyCache.has(cacheKey)) return companyCache.get(cacheKey);

      const promise = (async () => {
        if (!shopId) return null;
        const urlFor = (c) => `/order/company-invoice-details/?shop_id=${shopId}&city=${encodeURIComponent(c || '')}`;
        let data = null;
        try { data = (await api.get(urlFor(city))).data || null; } catch (e) { /* try next */ }
        if (!hasBankInfo(data)) {
          try {
            const res = await api.get(urlFor(''));
            if (hasBankInfo(res.data)) data = { ...(data || {}), ...res.data };
          } catch (e) { /* ignore */ }
        }
        return data;
      })();
      companyCache.set(cacheKey, promise);
      return promise;
    };

    const sent = [];
    const failed = [];
    let prepared = 0;

    for (const group of groups) {
      const ready = [];

      // 1) every order of this customer -> PDF
      for (const order of group.orders) {
        try {
          const [prodRes, company] = await Promise.all([
            api.get(`/order/orderdProducts/${order.order_Id}`),
            getCompany(order),
          ]);
          const products = transformOrderedProducts(prodRes.data);
          const pdf = buildInvoicePdfBase64({ order, products, company, logo, formatPrice });
          ready.push({
            order,
            payload: {
              order_id: order.order_Id,
              invoice_number: getInvoiceNumber(order),
              tracking_number: order.order_Tracking_Id || '',
              pdf_base64: pdf,
            },
          });
        } catch (error) {
          failed.push({ order, reason: extractServerErrorMessage(error, 'Could not prepare the invoice PDF.') });
        }
        prepared += 1;
        updateProgress(prepared);
      }

      if (ready.length === 0) continue;

      // 2) ONE email for this customer with all the PDFs attached
      try {
        const res = await api.post(
          '/order/invoice/send-batch-email/',
          { recipient_email: group.email, note, invoices: ready.map(r => r.payload) },
          { timeout: 120000 }
        );
        if (res.data && res.data.success === false) {
          const reason = res.data.message || 'Failed to send the email.';
          ready.forEach(r => failed.push({ order: r.order, reason }));
        } else {
          ready.forEach(r => sent.push(r.order));
        }
      } catch (error) {
        if (error?.response?.status === 404) {
          // Batch endpoint not available on the server: fall back to one email per order
          for (const r of ready) {
            try {
              await api.post(`/order/invoice/${r.order.order_Id}/send-email/`, { to: group.email });
              sent.push(r.order);
            } catch (err) {
              failed.push({ order: r.order, reason: extractServerErrorMessage(err, 'Could not send the invoice email.') });
            }
          }
        } else {
          const reason = extractServerErrorMessage(error, 'Could not send the invoice email.');
          ready.forEach(r => failed.push({ order: r.order, reason }));
        }
      }
    }

    setBulkBusy(false);
    removeFromSelection(sent); // failed ones stay selected so they can be retried
    if (failed.length === 0) setSendNote('');

    // ---- summary ----
    const batchLines = groups
      .filter(g => g.orders.length > 1 && g.orders.every(o => sent.includes(o)))
      .map(g => `<p style="margin:3px 0;font-size:13px">• ${escapeHtml(g.email)}: ${g.orders.length} invoices in 1 email</p>`)
      .join('');
    const batchHtml = batchLines
      ? `<div style="margin-top:10px;padding:10px;background:#e7f3ff;border-radius:6px;text-align:left">
           <p style="margin:0 0 4px 0;font-weight:600;color:#004aad">Batch Summary:</p>${batchLines}</div>`
      : '';

    const failedHtml = failed.length
      ? `<div style="text-align:left;font-size:13px;margin-top:8px;color:#dc2626;max-height:180px;overflow-y:auto">
           ${failed.slice(0, 8).map(f => `<p><b>${escapeHtml(f.order.order_Tracking_Id || f.order.order_Id)}</b>: ${escapeHtml(f.reason)}</p>`).join('')}
           ${failed.length > 8 ? `<p>+${failed.length - 8} more failed</p>` : ''}
         </div>`
      : '';

    const skippedHtml = skipped.length
      ? `<p style="margin-top:8px;color:#b45309;font-size:13px;text-align:left">${skipped.length} order(s) skipped (no valid customer email): ${previewList(skipped)}</p>`
      : '';

    Swal.fire({
      icon: failed.length === 0 ? 'success' : (sent.length === 0 ? 'error' : 'warning'),
      title: failed.length === 0 ? 'Invoices Sent!' : (sent.length === 0 ? 'Failed to Send' : 'Partly Sent'),
      html: `<div style="font-size:14px"><p><b>${sent.length}</b> invoice(s) sent${failed.length ? `, <b>${failed.length}</b> failed` : ''}.</p>${batchHtml}${failedHtml}${skippedHtml}</div>`,
      confirmButtonColor: '#004aad'
    });
  };

  const handleBulkDelete = async () => {
    if (bulkBusy || selectedCount === 0) return;
    const list = [...selectedOrders];

    const confirm = await Swal.fire({
      title: 'Delete Orders',
      html: `
        <div style="text-align:left;font-size:14px;line-height:1.6">
          <p>Are you sure you want to delete <b>${list.length}</b> selected order(s)? This cannot be undone.</p>
          <p style="color:#64748b;font-size:13px;word-break:break-word">${previewList(list, 8)}</p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#004aad',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });
    if (!confirm.isConfirmed) return;

    setBulkBusy(true);
    Swal.fire({
      title: 'Deleting Orders...',
      html: `<div style="font-size:15px"><b id="bulk-progress">0</b> / ${list.length}</div>`,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => { Swal.showLoading(); }
    });

    const deleted = [];
    const failed = [];
    const BATCH = 5;
    for (let i = 0; i < list.length; i += BATCH) {
      const chunk = list.slice(i, i + BATCH);
      const results = await Promise.allSettled(chunk.map(o => api.delete(`/order/delete/${o.order_Id}/`)));
      results.forEach((r, idx) => {
        if (r.status === 'fulfilled') deleted.push(chunk[idx]);
        else failed.push({ order: chunk[idx], reason: extractServerErrorMessage(r.reason, 'Could not delete.') });
      });
      updateProgress(Math.min(i + BATCH, list.length));
    }

    removeFromSelection(deleted);
    await fetchOrders();
    setBulkBusy(false);

    const failedHtml = failed.length
      ? `<div style="text-align:left;font-size:13px;margin-top:8px;color:#dc2626">
           ${failed.slice(0, 5).map(f => `<p><b>${escapeHtml(f.order.order_Tracking_Id || f.order.order_Id)}</b>: ${escapeHtml(f.reason)}</p>`).join('')}
           ${failed.length > 5 ? `<p>+${failed.length - 5} more failed</p>` : ''}
         </div>`
      : '';

    Swal.fire({
      icon: failed.length === 0 ? 'success' : (deleted.length === 0 ? 'error' : 'warning'),
      title: failed.length === 0 ? 'Deleted!' : (deleted.length === 0 ? 'Failed to Delete' : 'Partly Deleted'),
      html: `<div style="font-size:14px"><p><b>${deleted.length}</b> order(s) deleted${failed.length ? `, <b>${failed.length}</b> failed` : ''}.</p>${failedHtml}</div>`,
      confirmButtonColor: '#004aad',
      ...(failed.length === 0 ? { timer: 2000, showConfirmButton: false } : {})
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const getCustomerName = (customer) => {
    if (!customer) return '-';
    const firstName = customer.first_Name || customer.firstName || '';
    const lastName = customer.last_Name || customer.lastName || '';
    return `${firstName} ${lastName}`.trim() || customer.email || '-';
  };

  const getAddressDisplay = (address) => {
    if (!address) return '-';
    const houseNo = address.house_Number || address.houseNumber || '';
    const streetNo = address.street_Number || address.streetNumber || '';
    const area = address.area || '';
    const city = address.city || '';
    return `${city} - ${area}${streetNo ? ', St: ' + streetNo : ''}${houseNo ? ', House: ' + houseNo : ''}`;
  };

  const currencySymbol = getCurrencySymbol();

  return (
    <div className="orders-main-container">
      <div className="orders-header-section">
        <div className="orders-header-row-1">
          <div className="orders-header-left">
            <FaBoxOpen className="orders-header-icon" />
            <h2 className="orders-header-title">Orders</h2>
            <span className="shop-city-badge">
              <FaMapMarkerAlt className="shop-city-icon" />
              <span className="shop-city-name">
                {cityFilter
                  ? `${cityFilter} (${cityCounts[cityFilter] || 0})`
                  : `All Cities (${filteredOrders.length})`}
              </span>
            </span>
          </div>

          <div className="orders-header-right">
            <div className="city-dropdown-wrapper">
              <select
                className="city-select-dropdown"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                disabled={availableCities.length === 0}
              >
                <option value="">All Cities</option>
                {availableCities.map((city, index) => (
                  <option key={index} value={city}>
                    {city} ({cityCounts[city] || 0})
                  </option>
                ))}
              </select>
              <IoMdArrowDropdown className="city-dropdown-icon" />
            </div>

            <div className="search-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search by Tracking No./Customer/Ref."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="currency-indicator">
              Currency: {getCurrentCurrency()} ({currencySymbol})
            </div>
          </div>
        </div>

        {/* FILTER BAR */}
        <div className="orders-filter-bar">
          <select
            className="filter-control status-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Status</option>
            <option value="Processing">Processing</option>
            <option value="Shipped">Shipped</option>
            <option value="Pending">Pending</option>
            <option value="Delivered">Delivered</option>
            <option value="Canceled">Canceled</option>
            <option value="Returned">Returned</option>
          </select>

          <input
            type="date"
            className="filter-control date-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="dd/mm/yyyy"
          />

          <input
            type="date"
            className="filter-control date-input"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="dd/mm/yyyy"
          />

          <select
            className="filter-control customer-select"
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
          >
            <option value="">Select Customer</option>
            {availableCustomers.map((customer, index) => (
              <option key={index} value={customer}>{customer}</option>
            ))}
          </select>

          <button className="btn-apply-filter" onClick={handleApplyFilters}>
            <FaFilter /> Apply Filters & Select All
          </button>

          <button className="btn-clear-filter" onClick={handleClearFilters}>
            <FaUndo /> Clear Filters
          </button>
        </div>
      </div>

      <div className="orders-content">
        {/* Bulk actions: only when at least one order is selected */}
        {selectedCount > 0 && (
          <div className="orders-bulk-bar">
            <button className="btn-bulk-delete" onClick={handleBulkDelete} disabled={bulkBusy}>
              <FaTrash /> Delete ({selectedCount})
            </button>
            <button className="btn-send-invoice" onClick={openSendModal} disabled={bulkBusy}>
              <FaEnvelope /> Send Invoice ({selectedCount})
            </button>
          </div>
        )}

        <div className="orders-main-box">
          <div className="orders-table-container">
            <div className="orders-table-wrapper">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th className="select-col">
                      <input
                        type="checkbox"
                        className="order-checkbox"
                        checked={allPageSelected}
                        ref={(el) => { if (el) el.indeterminate = somePageSelected; }}
                        onChange={togglePage}
                        disabled={currentOrders.length === 0}
                        title="Select all on this page"
                      />
                    </th>
                    <th>Date</th>
                    <th>Order Tracking ID</th>
                    <th>Customer Reference</th>
                    <th>Customer</th>
                    <th>City</th>
                    <th>Status</th>
                    <th>Total ({currencySymbol})</th>
                    <th>Note</th>
                    <th>Address</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Nothing is shown until the first load is done, so "No orders found" never flashes */}
                  {isLoading ? (
                    <tr>
                      <td colSpan="11" style={{ height: '200px' }}></td>
                    </tr>
                  ) : currentOrders.length > 0 ? (
                    currentOrders.map((order) => (
                      <tr key={order.order_Id} className={selectedIds.has(order.order_Id) ? 'row-selected' : ''}>
                        <td className="select-cell">
                          <input
                            type="checkbox"
                            className="order-checkbox"
                            checked={selectedIds.has(order.order_Id)}
                            onChange={() => toggleOrder(order.order_Id)}
                          />
                        </td>
                        <td>{formatDate(order.order_Placment_Date)}</td>
                        <td className="order-tracking-cell">{order.order_Tracking_Id || '-'}</td>
                        <td>{order.customer_Reference || '-'}</td>
                        <td>{getCustomerName(order.customer)}</td>
                        <td>{order.city || 'All Cities'}</td>
                        <td>
                          <span className={`status-badge ${order.order_Status ? order.order_Status.replace(/\s/g, '-').toLowerCase() : ''}`}>
                            {order.order_Status || '-'}
                          </span>
                        </td>
                        <td className="total-amount-cell">
                          {formatOrderTotal(calculateOrderTotal(order))}
                        </td>
                        <td className="note-cell" title={getOrderNoteDisplay(order)}>
                          {getOrderNoteDisplay(order)}
                        </td>
                        <td className="address-cell">{getAddressDisplay(order.address)}</td>
                        <td className="actions-cell">
                          <div className="actions-cell-inner">
                            <button
                              className="edit-order-btn"
                              onClick={() => handleEditClick(order)}
                              title="Edit Order"
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="delete-order-btn"
                              onClick={() => handleDeleteClick(order)}
                              title="Delete Order"
                            >
                              <RiDeleteBin5Fill />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="no-data-cell">
                        <FaBuilding className="no-data-icon" />
                        <p>No orders found.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination: attached to the bottom of the table */}
            {filteredOrders.length > 0 && (
              <div className="orders-pagination">
                <button
                  className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`}
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="page-info">Page {currentPage} of {totalPages}</span>
                <button
                  className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`}
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ============================================================
          SEND INVOICES MODAL
          ============================================================ */}
      {showSendModal && (
        <div className="sim-overlay" onClick={closeSendModal}>
          <div className="sim-container" onClick={(e) => e.stopPropagation()}>
            <div className="sim-header">
              <h3><FaEnvelope style={{ marginRight: 10 }} />Send Invoices</h3>
              <button className="sim-x" onClick={closeSendModal} disabled={bulkBusy}><FaTimes /></button>
            </div>

            <div className="sim-body">
              {sendableGroups.some(g => g.orders.length > 1) && (
                <div className="sim-banner">
                  <FaInfoCircle style={{ marginRight: 8, flexShrink: 0 }} />
                  <span><strong>Batch Sending Active:</strong> Multiple invoices for the same customer will be sent in a single email.</span>
                </div>
              )}

              <div className="sim-summary">
                <p className="sim-count">
                  You have selected <strong>{selectedCount}</strong> order(s) to send invoices
                </p>
                <p className="sim-sub">
                  {sendableGroups.length} customer(s) will receive {sendableGroups.map(g => g.orders.length).join(', ') || 0} invoice(s) each
                </p>
              </div>

              <h4 className="sim-h4">Selected Orders by Customer:</h4>

              {sendableGroups.map((group) => (
                <div key={group.key} className="sim-group">
                  <div className="sim-group-head">
                    <span className="sim-name"><FaUser style={{ marginRight: 6 }} />{group.customerName}</span>
                    <span className="sim-email"><FaEnvelope style={{ marginRight: 6 }} />{group.email}</span>
                    <span className="sim-badge">
                      <FaFileInvoice style={{ marginRight: 4 }} />
                      {group.orders.length} invoice{group.orders.length > 1 ? 's' : ''}{group.orders.length > 1 ? ' 📦' : ''}
                    </span>
                  </div>
                  <ul>
                    {group.orders.map((order, i) => (
                      <li key={order.order_Id} className="sim-order">
                        <span className="sim-num">{i + 1}.</span>
                        <span><strong>Tracking:</strong> {order.order_Tracking_Id || 'N/A'}</span>
                        <span><strong>Amount:</strong> {formatOrderTotal(calculateOrderTotal(order))}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {skippedGroups.length > 0 && (
                <div className="sim-skipped">
                  <p><FaExclamationTriangle style={{ marginRight: 6 }} /><strong>Will be skipped</strong> (customer email missing or invalid):</p>
                  <ul>
                    {skippedGroups.map((g) => (
                      <li key={g.key}>
                        {g.customerName}{g.email ? ` (${g.email})` : ''}: {g.orders.map(o => o.order_Tracking_Id || o.order_Id).join(', ')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="sim-note">
                <label>Note (optional):</label>
                <textarea
                  rows={3}
                  value={sendNote}
                  onChange={(e) => setSendNote(e.target.value)}
                  placeholder="Add a note to include in the email (optional)"
                />
              </div>

              <div className="sim-total">
                Total Amount: <strong>{formatOrderTotal(sendableTotal)}</strong>
              </div>
            </div>

            <div className="sim-footer">
              <button className="sim-cancel" onClick={closeSendModal} disabled={bulkBusy}>
                <FaTimes style={{ marginRight: 6 }} />Cancel
              </button>
              <button className="sim-send" onClick={handleConfirmSend} disabled={bulkBusy || sendableOrdersCount === 0}>
                <IoMdSend style={{ marginRight: 6 }} />Send Invoices
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .sim-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; justify-content: center; align-items: center; z-index: 1000; }
        .sim-container { background: #fff; border-radius: 12px; max-width: 800px; width: 92%; max-height: 88vh; display: flex; flex-direction: column; box-shadow: 0 20px 60px rgba(0,0,0,.3); }
        .sim-header { display: flex; justify-content: space-between; align-items: center; padding: 18px 24px; border-bottom: 1px solid #e0e0e0; }
        .sim-header h3 { margin: 0; color: #004aad; font-size: 20px; display: flex; align-items: center; }
        .sim-x { background: none; border: none; font-size: 22px; cursor: pointer; color: #666; display: flex; }
        .sim-x:hover:not(:disabled) { color: #222; }
        .sim-body { padding: 22px 24px; overflow-y: auto; flex: 1; }
        .sim-banner { background: #e7f3ff; color: #004aad; padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; border-left: 4px solid #004aad; font-size: 14px; display: flex; align-items: flex-start; }
        .sim-summary { background: #f8f9fa; padding: 16px; border-radius: 8px; margin-bottom: 18px; }
        .sim-count { margin: 0; font-size: 16px; color: #333; }
        .sim-count strong { color: #28a745; font-size: 18px; }
        .sim-sub { margin: 8px 0 0; font-size: 14px; color: #666; }
        .sim-h4 { margin: 0 0 12px; font-size: 16px; color: #333; }
        .sim-group { margin-bottom: 12px; border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden; }
        .sim-group-head { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; padding: 10px 14px; background: #f0f7ff; border-bottom: 1px solid #e0e0e0; }
        .sim-name { font-weight: 600; color: #004aad; font-size: 15px; display: flex; align-items: center; }
        .sim-email { font-size: 14px; color: #555; display: flex; align-items: center; word-break: break-all; }
        .sim-badge { background: #004aad; color: #fff; padding: 4px 12px; border-radius: 20px; font-size: 13px; font-weight: 500; margin-left: auto; display: flex; align-items: center; }
        .sim-group ul, .sim-skipped ul { list-style: none; padding: 0; margin: 0; }
        .sim-order { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; padding: 10px 16px; background: #fafafa; border-bottom: 1px solid #f0f0f0; font-size: 14px; }
        .sim-order:last-child { border-bottom: none; }
        .sim-num { font-weight: 600; color: #004aad; min-width: 26px; }
        .sim-skipped { background: #fff8e6; border: 1px solid #ffe0a3; border-radius: 8px; padding: 12px 14px; margin: 14px 0; font-size: 13px; color: #8a5a00; }
        .sim-skipped p { margin: 0 0 6px; }
        .sim-skipped li { margin: 3px 0; }
        .sim-note { margin: 16px 0; display: flex; flex-direction: column; gap: 6px; }
        .sim-note label { font-weight: 600; color: #004aad; font-size: 14px; }
        .sim-note textarea { padding: 8px 12px; border: 1px solid #ced4da; border-radius: 6px; font-size: 14px; font-family: inherit; resize: vertical; }
        .sim-total { background: #e7f3ff; padding: 14px 16px; border-radius: 8px; text-align: right; border: 1px solid #b3d9ff; color: #004aad; font-size: 16px; }
        .sim-total strong { font-size: 18px; }
        .sim-footer { display: flex; justify-content: flex-end; gap: 12px; padding: 14px 24px; border-top: 1px solid #e0e0e0; background: #f8f9fa; border-radius: 0 0 12px 12px; }
        .sim-cancel, .sim-send { padding: 10px 24px; border: none; border-radius: 6px; cursor: pointer; font-size: 14px; font-weight: 600; color: #fff; display: flex; align-items: center; }
        .sim-cancel { background: #6c757d; }
        .sim-send { background: linear-gradient(135deg, #28a745, #20c997); }
        .sim-cancel:disabled, .sim-send:disabled { opacity: .6; cursor: not-allowed; }
        @media (max-width: 768px) {
          .sim-order { flex-direction: column; align-items: flex-start; gap: 4px; }
          .sim-group-head { flex-direction: column; align-items: flex-start; }
          .sim-badge { margin-left: 0; }
          .sim-footer { flex-direction: column-reverse; }
          .sim-cancel, .sim-send { width: 100%; justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default Orders;